import React, { useState, useEffect } from "react";
import "./RightPanel.css";
import { useTranslation } from "react-i18next";
import {
  FaExternalLinkAlt,
  FaCalendarAlt,
  FaVideo,
  FaFilter,
  FaChevronDown,
} from "react-icons/fa";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../config/firebase";

function RightPanel({ onScroll }) {
  const { t, i18n } = useTranslation();
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [newsData, setNewsData] = useState([]);

  const currentLang =
    i18n.language && i18n.language.startsWith("en") ? "en" : "fr";

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "events"), (snapshot) => {
      const events = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setNewsData(events);
    });
    return () => unsubscribe();
  }, []);

  const truncateText = (text, limit) => {
    if (!text) return "";
    return text.length > limit ? text.slice(0, limit) + "..." : text;
  };

  const handleFilterSelect = (filterType) => {
    setActiveFilter(filterType);
    setFilterOpen(false);
  };

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const filteredData = newsData.filter((item) => {
    if (!item.isoDate) return false;
    const itemDate = new Date(item.isoDate);
    if (activeFilter === "NEXT_MONTH") {
      const nextMonth = new Date(now);
      nextMonth.setMonth(now.getMonth() + 1);
      return itemDate >= now && itemDate <= nextMonth;
    }
    if (activeFilter === "NEXT_3_MONTHS") {
      const next3Months = new Date(now);
      next3Months.setMonth(now.getMonth() + 3);
      return itemDate >= now && itemDate <= next3Months;
    }
    if (activeFilter === "THIS_YEAR")
      return itemDate.getFullYear() === now.getFullYear() && itemDate >= now;
    return true;
  });

  const upcomingEvents = filteredData
    .filter((item) => new Date(item.isoDate) >= now)
    .sort((a, b) => new Date(a.isoDate) - new Date(b.isoDate));

  const pastEvents = filteredData
    .filter((item) => new Date(item.isoDate) < now)
    .sort((a, b) => new Date(b.isoDate) - new Date(a.isoDate));

  const EventCard = ({ item }) => {
    const title =
      item[`title_${currentLang}`] || item.title || item.title_fr || "";
    const description =
      item[`desc_${currentLang}`] || item.desc || item.desc_fr || "";

    return (
      <div className="news-card">
        <div className="card-image">
          <img src={item.image} alt={title} />
          <span className={`badge ${item.type.toLowerCase()}`}>
            {item.type}
          </span>
        </div>

        <div className="card-content">
          <h4>{title}</h4>
          <div className="card-body-split">
            <div className="split-left">
              <p className="card-desc">{truncateText(description, 80)}</p>
            </div>
            <div className="split-right">
              <div className="date-badge">
                <FaCalendarAlt className="cal-icon" />
                <span>{item.date}</span>
              </div>
              {item.link && (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noreferrer"
                  className="read-more-btn"
                >
                  {t("right_panel.read_more")} <FaExternalLinkAlt size={10} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <aside className="right-panel glass-panel">
      <div className="rp-header">
        <h3>
          {t("right_panel.title")}{" "}
          <span className="gradient-text">{t("right_panel.title_span")}</span>
        </h3>
        <div className="filter-container">
          <button
            className="filter-btn"
            onClick={() => setFilterOpen(!filterOpen)}
          >
            <FaFilter />
            {activeFilter === "ALL" && t("right_panel.filter_all")}
            {activeFilter === "NEXT_MONTH" &&
              t("right_panel.filter_next_month")}
            {activeFilter === "NEXT_3_MONTHS" &&
              t("right_panel.filter_next_3_months")}
            {activeFilter === "THIS_YEAR" && t("right_panel.filter_this_year")}
            <FaChevronDown
              size={10}
              style={{
                marginLeft: "5px",
                transform: filterOpen ? "rotate(180deg)" : "none",
                transition: "0.3s",
              }}
            />
          </button>

          {filterOpen && (
            <div className="filter-dropdown">
              <div
                className={`filter-option ${activeFilter === "ALL" ? "active" : ""}`}
                onClick={() => handleFilterSelect("ALL")}
              >
                {t("right_panel.filter_all")}
              </div>
              <div
                className={`filter-option ${activeFilter === "NEXT_MONTH" ? "active" : ""}`}
                onClick={() => handleFilterSelect("NEXT_MONTH")}
              >
                {t("right_panel.filter_next_month")}
              </div>
              <div
                className={`filter-option ${activeFilter === "NEXT_3_MONTHS" ? "active" : ""}`}
                onClick={() => handleFilterSelect("NEXT_3_MONTHS")}
              >
                {t("right_panel.filter_next_3_months")}
              </div>
              <div
                className={`filter-option ${activeFilter === "THIS_YEAR" ? "active" : ""}`}
                onClick={() => handleFilterSelect("THIS_YEAR")}
              >
                {t("right_panel.filter_this_year")}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="news-scroll" onScroll={onScroll}>
        <div className="news-feed">
          <div className="featured-card">
            <div className="featured-row-title">
              <FaVideo className="title-icon" />
              <h4>{t("webinar_card.title")}</h4>
            </div>
            <p className="featured-text">{t("webinar_card.desc")}</p>
            <div className="featured-date-capsule">
              <FaCalendarAlt />
              <span>{t("webinar_card.date")}</span>
            </div>
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSfZvXA7erDV05dw97cbC55BpcFQTip8fm8vM9bpEZE3Z4x7-w/viewform?usp=dialog"
              target="_blank"
              rel="noreferrer"
              className="featured-cta-button"
            >
              {t("webinar_card.cta")}
            </a>
          </div>
          {upcomingEvents.map((item) => (
            <EventCard key={item.id} item={item} />
          ))}
          {pastEvents.length > 0 && (
            <div className="events-separator">
              {t("right_panel.past_events")}
            </div>
          )}
          {pastEvents.map((item) => (
            <EventCard key={item.id} item={item} />
          ))}
        </div>
      </div>
    </aside>
  );
}

export default RightPanel;
